<script setup>
import ImportKeywordsAction from "@frontJs/components/actions/projectManagement/seo/ImportKeywordsAction.vue";
import TrackedKeywordActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/TrackedKeywordActionsCell.vue";
import TrackedKeywordTargetsCell from "@frontJs/components/tables/cells/projectManagement/seo/TrackedKeywordTargetsCell.vue";
import { ArrowUpTrayIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CheckCircleIconCell from "@js/components/tables/cells/CheckCircleIconCell.vue";
import StatusCellV2 from "@js/components/tables/cells/StatusCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { tenant, websiteVariants, targetedLanguages, targetableGoogleWebsites } =
  defineProps({
    tenant: {
      type: Object,
      required: true,
    },
    websiteVariants: {
      type: Array,
      required: true,
    },
    targetedLanguages: {
      type: Array,
      required: true,
    },
    targetableGoogleWebsites: {
      type: Array,
      required: true,
    },
  });

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "keyword",
      header: () => t("label.keyword"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.keyword,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "isTracked",
      header: () => t("label.activeFollowUp"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.isTracked,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "importantToFollow",
      header: () => t("label.importantToFollow"),
      cell: (props) =>
        props.row.original.importantToFollow ? h(CheckCircleIconCell) : undefined,
    }),
    columnHelper.accessor(() => null, {
      id: "language",
      header: () => t("label.language"),
      cell: (props) =>
        props.row.original.language ? h(TextCellV2, {
          text: props.row.original.language.nameFr,
        }) : undefined,
    }),
    columnHelper.accessor(() => null, {
      id: "targets",
      header: () => t("label.targets"),
      cell: (props) =>
        h(TrackedKeywordTargetsCell, {
          targets: props.row.original.targets,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "lastRefreshedAt",
      header: () => t("label.lastUpdate"),
      cell: (props) =>
        props.row.original.lastRefreshAt ? h(TextCellV2, {
          text: props.row.original.lastRefreshAt,
        }) : undefined,
    }),
    columnHelper.display({
      id: "actions",
      header: () => t("label.actions"),
      cell: (innerProps) =>
        h(TrackedKeywordActionsCell, {
          tenant: tenant,
          row: innerProps.row,
          callback: updateData,
        }),
      width: 0,
    }),
  ],
  url: route("front.projectManagement.seo.postTrackedKeywords", {
    tenant: tenant.id,
  }),
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  >
    <template #actions>
      <ImportKeywordsAction
        :tenant="tenant"
        :targetable-google-websites="targetableGoogleWebsites"
        :website-variants="websiteVariants"
        :targeted-languages="targetedLanguages"
        :callback="updateData"
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            :callback="() => setIsOpen(true)"
            :left-icon="ArrowUpTrayIcon"
          >
            {{ t("button.import") }}
          </ActionButton>
        </template>
      </ImportKeywordsAction>
    </template>
  </DataTableLayout>
</template>
